import { clienteDisplay } from '@/lib/types';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { UserCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import type { Solicitante, Contrato, Cota } from '@/lib/types';

type GrupoKey = 'Conecta Folha Business' | 'BRM - BUY' | 'Setorial' | 'Pesquisa' | 'Apex / Research';

const GRUPO_COR: Record<GrupoKey, string> = {
  'Conecta Folha Business': 'border-blue/40',
  'BRM - BUY':              'border-purple/40',
  'Setorial':               'border-pink/40',
  'Pesquisa':               'border-green/40',
  'Apex / Research':        'border-green/40',
};

export function Portais() {
  const contratosQ = useQuery({ queryKey: ['contratos'], queryFn: () => api.contratos.list().then((r) => r.data) });
  const cotasQ = useQuery({ queryKey: ['cotas'], queryFn: () => api.cotas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });

  const contratos = contratosQ.data ?? [];
  const cotas = cotasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];

  const cotaById = useMemo(() => new Map<string, Cota>(cotas.map((c) => [c.id, c])), [cotas]);

  // Mapa solicitanteId -> grupo (família ou Apex/Research)
  const grupoPorSolicitante = useMemo(() => {
    const m = new Map<string, GrupoKey>();
    for (const c of contratos) {
      if (c.status !== 'Vigente' && c.status !== 'Renovação em curso') continue;
      const fam = c.cotaId ? cotaById.get(c.cotaId)?.familia : undefined;
      if (fam) m.set(c.solicitanteId, fam as GrupoKey);
    }
    for (const s of solicitantes) {
      if (m.has(s.id)) continue;
      if (s.tipo === 'Unidade Apex' || s.tipo === 'Time Research') {
        m.set(s.id, 'Apex / Research');
      }
    }
    return m;
  }, [contratos, cotaById, solicitantes]);

  const porGrupo = useMemo(() => {
    const m: Record<GrupoKey, { solicitante: Solicitante; contrato?: Contrato }[]> = {
      'Conecta Folha Business': [], 'BRM - BUY': [], 'Setorial': [], 'Pesquisa': [], 'Apex / Research': [],
    };
    for (const s of solicitantes) {
      const grp = grupoPorSolicitante.get(s.id);
      if (!grp) continue;
      const contrato = contratos.find((c) => c.solicitanteId === s.id && c.status === 'Vigente');
      m[grp].push({ solicitante: s, contrato });
    }
    for (const k of Object.keys(m) as GrupoKey[]) {
      m[k].sort((a, b) => a.solicitante.nome.localeCompare(b.solicitante.nome));
    }
    return m;
  }, [solicitantes, grupoPorSolicitante, contratos]);

  return (
    <div>
      <PageHeader title="Portais de cliente" />
      <div className="px-6 py-4 space-y-5">
        <p className="text-xs text-text-2">
          Escolha um cliente pra ver exatamente o que ele enxerga ao logar — entregas publicadas, peças aguardando aprovação,
          métricas do contrato. Visualização de leitura.
        </p>

        {(Object.keys(porGrupo) as GrupoKey[]).map((grupo) => {
          const list = porGrupo[grupo];
          if (!list.length) return null;
          return (
            <section key={grupo}>
              <h2 className="text-xs font-bold uppercase tracking-wider text-text-2 mb-2">{grupo}</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                {list.map(({ solicitante, contrato }) => (
                  <Link
                    key={solicitante.id}
                    to={`/portal?as=${solicitante.id}`}
                    className={clsx(
                      'bg-panel border border-border border-2 rounded-md p-3 hover:border-blue/50 hover:bg-panel-2 transition-colors',
                      GRUPO_COR[grupo],
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <UserCircle2 className="w-4 h-4 text-muted mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-bold leading-tight line-clamp-2 mb-1">{clienteDisplay(solicitante.nome)}</div>
                        {contrato && (
                          <div className="text-[10px] text-muted">{contrato.identificador}</div>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
